import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import cloneDeep from 'lodash/cloneDeep';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';

import { FormControlTypes, CountryDropdown } from 'modules/Common/utils/Utility';
import { gridSpanClass } from 'modules/Common/form/gridSpanClass';
import CgPopover from 'modules/Common/ui/CgPopover';
import CgInfoHint from 'modules/Common/ui/CgInfoHint';
import Label from '../primitives/Label';
import ErrorText from '../primitives/ErrorText';
import { sectionTitleClass } from '../primitives/inputClasses';
import { gapAnchor } from 'modules/Common/utils/gapAnchor';
import { isEmptyControlValue } from 'modules/Common/utils/controlValue';
import ControlAnchor from '../primitives/ControlAnchor';

import { useFieldEditing, FieldPresenceRing } from 'modules/Common/presence/FieldPresence';
import TextField from '../fields/TextField';
import SelectField from '../fields/SelectField';
import SearchSelectField from '../fields/SearchSelectField';
import CountrySelectField from '../fields/CountrySelectField';
import GoodsSelectField from '../fields/GoodsSelectField';
import PhoneField from '../fields/PhoneField';
import CheckboxField from '../fields/CheckboxField';
import ToggleField from '../fields/ToggleField';
import HiddenField from '../fields/HiddenField';

// Adapter that consumes the v1 CgForm `formGroups` config and renders it
// using v2 primitives. The state engine is ported (almost verbatim) from
// the v1 CgForm.jsx — keeping the well-tested cascading dependency,
// fetchValue/fetchOptions and possibleControls semantics — and only the
// rendering layer is swapped out to use the new Tailwind/Radix fields.
//
// We deliberately do NOT use react-hook-form here: the v1 engine is
// sufficient for the legacy config shape, and reproducing its async
// cascading behaviour with `watch`/`setValue` adds risk without benefit.
// New code should use the composable `<Form>` API instead.

// A fetchValue may return either a bare value (every legacy config does) or a
// `{ value, meta }` envelope when it can also say where the value came from.
// `meta` is `{ bucket, key, data }` and is routed verbatim into form data, so
// this file never learns what any of it means.
const isMetaEnvelope = (v) =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v) && 'value' in v && 'meta' in v;

// An entry whose data is `null` is removed: the envelope says the value no longer has that
// provenance (a fetch that found nothing), which a spread could never express.
const mergeBuckets = (current, buckets) => {
  const out = {};
  for (const [bucket, entries] of Object.entries(buckets)) {
    const merged = { ...current[bucket] };
    for (const [key, data] of Object.entries(entries)) {
      if (data === null) delete merged[key];
      else merged[key] = data;
    }
    out[bucket] = merged;
  }
  return out;
};

const hasDependency = (control, fieldName) =>
  control.dependency === fieldName ||
  (control.dependencies && control.dependencies.includes(fieldName));

// Map a v1 control to its v2 field component + props.
function renderField(control, field, fieldState, disabled) {
  const common = {
    field,
    fieldState,
    id: control.name,
    disabled,
    placeholder: control.placeholder,
  };
  switch (control.type) {
    case FormControlTypes.Numeric:
      return (
        <TextField
          {...common}
          numeric
        />
      );
    case FormControlTypes.Text: {
      // v1 CgInput supports a `textArea` flag on the control config.
      return control.textArea ? (
        <TextField
          {...common}
          textarea
        />
      ) : (
        <TextField {...common} />
      );
    }
    case FormControlTypes.Dropdown:
      return (
        <SelectField
          {...common}
          options={control.options || []}
          valueKey={control.optionValue || 'code'}
          labelKey={control.optionLabel || 'description'}
        />
      );
    case FormControlTypes.DropdownSelect:
      // CgForm path — match v1 CgSelectDropdown:
      //   - Always read option.label / option.value. v1
      //     CgSelectDropdown.jsx:62-66 filters on `option?.label` and
      //     line 111 finds by `option.value == formControl.value`. It
      //     ignored optionLabel/optionValue config; respecting them
      //     would break configs whose options don't have those keys.
      //   - storeAs is `object` for isMulti, `value` for single — that
      //     matches v1 CgSelectDropdown.handleChange (line 115-122)
      //     which extracts `.value` for single but stores the whole
      //     options array for multi.
      //   - Honor creatable for Fuel / Raw / Intermediate / SubProcess.
      return (
        <SearchSelectField
          {...common}
          options={control.options || []}
          valueKey="value"
          labelKey="label"
          isMulti={control.isMulti}
          storeAs={control.isMulti ? 'object' : 'value'}
          creatable={!!control.creatable}
        />
      );
    case FormControlTypes.OODropdown:
      // Operator-only countries — preserve v1 behavior. Same hardcoded
      // label/value contract as DropdownSelect.
      return (
        <SearchSelectField
          {...common}
          options={control.options || []}
          valueKey="value"
          labelKey="label"
        />
      );
    case FormControlTypes.EUDropdown:
      return (
        <CountrySelectField
          {...common}
          scope={CountryDropdown.EU}
        />
      );
    case FormControlTypes.ALLDropdown:
      return (
        <CountrySelectField
          {...common}
          scope={CountryDropdown.ALL}
        />
      );
    case FormControlTypes.CBAMDropdown:
      return (
        <CountrySelectField
          {...common}
          scope={CountryDropdown.CBAM}
        />
      );
    case FormControlTypes.GoodsDropdown:
      return <GoodsSelectField {...common} />;
    case FormControlTypes.Phone:
      return <PhoneField {...common} />;
    case FormControlTypes.Checkbox:
      return (
        <CheckboxField
          {...common}
          inlineLabel={control.label}
        />
      );
    case FormControlTypes.Toggle:
      return (
        <ToggleField
          {...common}
          offLabel={control.offLabel}
          onLabel={control.onLabel}
          sizing={control.sizing}
        />
      );
    case FormControlTypes.Hidden:
      return (
        <HiddenField
          field={field}
          id={control.name}
        />
      );
    default:
      return <TextField {...common} />;
  }
}

// Visual wrapper around one rendered field: label + popover + error +
// optional "New" badge, mirroring v1 CgInput's surface.
function FieldWrapper({
  control,
  value,
  error,
  fieldLoading,
  disabled,
  onChange,
  onBlur,
  anchorKind,
}) {
  const { t } = useTranslation('common');
  // P12/D30 — field-level editing presence: publish which field THIS user is in (focus/change/blur)
  // and render a peer's advisory ring when they're on it. No-op with no FieldPresenceProvider mounted
  // (a form outside a live diagram is unaffected). `control.name` is the shared fieldKey.
  const fieldPresence = useFieldEditing(control.name);
  const rootRef = useRef(null);
  if (control.shouldBeHidden && control.shouldBeHidden(value?.formData)) return null;

  const colClass = gridSpanClass(control.className);

  // Trigger controls render only an info popover, no input.
  if (control.type === FormControlTypes.Trigger) {
    return (
      <div className={`${colClass} flex items-center gap-2 px-2 py-2`}>
        <CgPopover
          trigger="hover"
          position="top"
          triggerElement={
            <span className="text-slate-500 cursor-help inline-flex">
              <FontAwesomeIcon icon={faIcons.faQuestionCircle} />
            </span>
          }
          popoverContent={<div className="text-sm">{control.text}</div>}
        />
        {control.isNew ? (
          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-primaryColor text-white rounded-md">
            {t('common:badge.new', 'New')}
          </span>
        ) : null}
      </div>
    );
  }

  if (control.type === FormControlTypes.Hidden) {
    // Hidden fields don't render any chrome.
    return renderField(
      control,
      { value, onChange: (v) => onChange(v), onBlur, name: control.name },
      { error: undefined },
      disabled,
    );
  }

  const synthField = {
    name: control.name,
    value,
    // Call the form's own onChange AND publish "actively changing this field" (intense ring on peers).
    onChange: (v) => {
      onChange(v);
      fieldPresence.onChange();
    },
    onBlur,
    ref: undefined,
  };
  const synthState = error ? { error: { message: error } } : { error: undefined };

  const hasInfo = control.popoverTitle || control.popoverContent;

  return (
    // onFocus/onBlur on the wrapper capture descendant-input focus (React normalizes focusin/out to
    // bubbling onFocus/onBlur) → publish focus/blur without touching each field component.
    <div
      ref={rootRef}
      className={`${colClass} mb-3`}
      onFocus={fieldPresence.onFocus}
      onBlur={fieldPresence.onBlur}
    >
      {control.label && control.type !== FormControlTypes.Checkbox ? (
        <div className="flex items-center gap-1.5 mb-1">
          <Label
            htmlFor={control.name}
            className="mb-0"
          >
            {control.label}
          </Label>
          {control.isNew ? (
            <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-primaryColor text-white rounded-md">
              {t('common:badge.new', 'New')}
            </span>
          ) : null}
          {hasInfo ? (
            <CgInfoHint
              title={control.popoverTitle}
              position="top"
              subject={control.label}
            >
              {control.popoverContent}
            </CgInfoHint>
          ) : null}
          {fieldLoading ? (
            <FontAwesomeIcon
              icon={faIcons.faSpinner}
              spin
              size="sm"
              className="text-primaryColor ml-auto"
            />
          ) : null}
        </div>
      ) : null}
      <FieldPresenceRing fieldKey={control.name}>
        {renderField(control, synthField, synthState, disabled)}
      </FieldPresenceRing>
      {anchorKind ? (
        <ControlAnchor
          rootRef={rootRef}
          name={control.name}
          anchor={gapAnchor(anchorKind, control.name)}
          empty={isEmptyControlValue(value)}
        />
      ) : null}
      <ErrorText>{error}</ErrorText>
    </div>
  );
}

const LegacyFormGroups = forwardRef(function LegacyFormGroups(
  {
    formGroups,
    onSubmit,
    onChange,
    submitting,
    loading,
    btnName = 'Save',
    btnIcon,
    hideButton,
    actions,
    initialState = {},
    formShown,
    readOnly = false,
    anchorKind,
    // External per-field errors merged with the internal Zod
    // errors at render time. Used for cross-field validations
    // (e.g. "Total quantity cannot be lower than attributed
    // quantity") that the per-control Zod schema can't express.
    // Shape: { [fieldName]: message }. External errors take
    // precedence — a Zod error and a cross-field error on the
    // same field surface the cross-field one because that's
    // usually the more actionable copy.
    extraErrors,
    // Extra content rendered after the configured groups and before the
    // Save button — for host-owned sections the config can't express.
    children,
  },
  ref,
) {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState(initialState);
  const [dynamicFormGroups, setDynamicFormGroups] = useState(formGroups);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [validationSchema, setValidationSchema] = useState(z.object({}));
  const [errors, setErrors] = useState({});
  const [loadingControls, setLoadingControls] = useState({});

  const formDataRef = useRef(formData);
  const prevFormDataRef = useRef(null);
  const resolveCounterRef = useRef(0);
  const shouldCallOnChangeRef = useRef(false);
  const unmountedRef = useRef(false);
  const errorsRef = useRef(errors);
  const onChangeRef = useRef(onChange);
  const validationSchemaRef = useRef(validationSchema);
  const touchedFieldsRef = useRef(new Set());
  const formGroupsRef = useRef(formGroups);
  // Two refs that together make the non-dependency auto-fetch effect
  // safe under React 18 <StrictMode>'s dev double-invoke:
  //   autoFetchInFlightRef — keys whose fetch has been kicked off but
  //                          hasn't resolved yet. Persists across the
  //                          StrictMode mount→cleanup→remount cycle
  //                          (refs survive), so the second mount sees
  //                          the key and skips re-firing.
  //   autoFetchedRef       — keys whose fetch resolved successfully
  //                          and mutated control.options.
  // Round 1 used a single "fetched" ref + a `cancelled` cleanup flag;
  // that combination silently ate the fetch in StrictMode (cleanup ran
  // before the async resolved, swallowing the mutation, and the second
  // mount saw the key already marked). Both keyed by
  // `${groupIdx}:${name}` so the same control name in two different
  // groups (e.g. swapped in by possibleControls) is tracked per slot.
  const autoFetchedRef = useRef(new Set());
  const autoFetchInFlightRef = useRef(new Set());

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    validationSchemaRef.current = validationSchema;
  }, [validationSchema]);

  useImperativeHandle(ref, () => ({
    updateField: (fieldName, newValue = '') => {
      if (newValue?.useHandleChange) {
        handleChange(fieldName, newValue.value);
      } else {
        setFormData((prev) => {
          if (prev[fieldName] === (newValue ?? '')) return prev;
          return { ...prev, [fieldName]: newValue ?? '' };
        });
      }
    },
    getFieldValue: (fieldName) => formDataRef.current[fieldName],
    showSubmitSuccess: () => setSubmitSuccess(true),
    handleSubmit: (e) => handleSubmit(e),
  }));

  // possibleFormGroups: legacy 'inputType' top-level swap.
  useEffect(() => {
    let newGroups = [];
    const baseGroups = formGroupsRef.current;
    baseGroups.forEach((group) => {
      if (group.groupDependency === 'inputType' && group.possibleFormGroups) {
        const selectedGroups = group.possibleFormGroups[formData.inputType] || [];
        newGroups.push(...cloneDeep(selectedGroups));
      }
    });
    setDynamicFormGroups([...baseGroups, ...newGroups]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.inputType]);

  // Build validation schema + sync possibleControls swap when groups change.
  useEffect(() => {
    let validations = {};
    const currentFormData = formDataRef.current;
    let newFormData = { ...currentFormData };
    let hasNewDefaults = false;
    let groupsToUse = dynamicFormGroups;

    // Sync each possibleControls group to formData's current
    // groupDependency value. This used to be gated by
    // `!prevFormDataRef.current` so it ran only on first mount, but that
    // lost the swap on CHP reopen: the inputType effect (line ~358) calls
    // setDynamicFormGroups([...baseGroups]) every time formData.inputType
    // resolves, which can reset the possibleControls group back to its
    // empty `controls: []` after the initial swap already fired. Running
    // an idempotent sync here restores the swap whenever
    // dynamicFormGroups gets rebuilt with the base controls but formData
    // still has a valid groupDependency value. The name-equality check
    // makes it a no-op when controls already match, so no infinite loop.
    let initialSwapNeeded = false;
    const updatedGroups = dynamicFormGroups.map((group) => {
      if (
        group.groupDependency &&
        group.groupDependency !== 'inputType' &&
        group.possibleControls
      ) {
        const value = currentFormData[group.groupDependency];
        if (value && value in group.possibleControls) {
          const expected = group.possibleControls[value];
          const sameControls =
            group.controls.length === expected.length &&
            group.controls.every((c, i) => c.name === expected[i]?.name);
          if (!sameControls) {
            initialSwapNeeded = true;
            return { ...group, controls: cloneDeep(expected) };
          }
        }
      }
      return group;
    });
    if (initialSwapNeeded) {
      groupsToUse = updatedGroups;
      setDynamicFormGroups(updatedGroups);
    }

    groupsToUse.forEach((group) => {
      group.controls.forEach((control) => {
        if (control.validation) validations[control.name] = control.validation;
        if (
          !(control.name in newFormData) ||
          (newFormData[control.name] === '' && control.defaultValue != null)
        ) {
          newFormData[control.name] = control.defaultValue ?? '';
          hasNewDefaults = true;
        }
      });
    });

    setValidationSchema(z.object(validations));
    if (hasNewDefaults) setFormData(newFormData);
    if (!prevFormDataRef.current) {
      prevFormDataRef.current = { ...(hasNewDefaults ? newFormData : currentFormData) };
    }
  }, [dynamicFormGroups]);

  // Auto-fetch fetchOptions for controls that don't declare a dependency.
  // v1 CgSelectDropdown / CgDropdown self-fetched in their own component
  // useEffect when options were empty (CgSelectDropdown.jsx:36). The v2
  // SearchSelectField is dumb — options come from props — so the form
  // adapter takes responsibility for kicking off the fetch on first
  // render. Without this, FuelFormConfig / RawFormConfig /
  // IntermediateFormConfig / SubProcess dropdowns open empty (their
  // fetchOptions never fire because they have no dependency).
  //
  // StrictMode-safe: see the comments on autoFetchInFlightRef above. We
  // dedup using the in-flight ref, mark "fetched" only after success,
  // and don't cancel on cleanup — mutation on the stable control
  // reference is idempotent, so letting StrictMode's first invocation
  // finish in the background after its synthetic cleanup is fine.
  useEffect(() => {
    const targets = [];
    dynamicFormGroups.forEach((group, groupIdx) => {
      group.controls.forEach((control) => {
        if (!control.fetchOptions) return;
        if (control.dependency || control.dependencies?.length) return;
        if (Array.isArray(control.options) && control.options.length > 0) return;
        const key = `${groupIdx}:${control.name}`;
        if (autoFetchedRef.current.has(key)) return;
        if (autoFetchInFlightRef.current.has(key)) return;
        autoFetchInFlightRef.current.add(key);
        targets.push({ control, groupIdx, key });
      });
    });

    if (targets.length === 0) return;

    (async () => {
      let anyOptionsFetched = false;
      for (const { control, key } of targets) {
        try {
          const next = await control.fetchOptions(undefined, {
            target: undefined,
          });
          // Mutate in place — same pattern as the cascading resolver
          // below. v1 CgForm did the same.
          control.options = Array.isArray(next) ? next : [];
          autoFetchedRef.current.add(key);
          anyOptionsFetched = true;
        } catch (err) {
          // Match the rest of the form's error handling — log + continue.
          console.error('fetchOptions error:', err);
        } finally {
          autoFetchInFlightRef.current.delete(key);
        }
      }
      if (anyOptionsFetched) {
        // Force a re-render so child fields pick up the new options.
        setDynamicFormGroups((prev) => [...prev]);
      }
    })();
  }, [dynamicFormGroups]);

  // Cascading dependency resolver — ported from v1 CgForm.
  useEffect(() => {
    if (!prevFormDataRef.current) return;

    const prevData = prevFormDataRef.current;
    const changedFields = [];
    const allKeys = new Set([...Object.keys(formData), ...Object.keys(prevData)]);
    for (const key of allKeys) {
      if (formData[key] !== prevData[key]) changedFields.push(key);
    }
    prevFormDataRef.current = { ...formData };

    if (changedFields.length === 0) {
      if (shouldCallOnChangeRef.current && onChangeRef.current) {
        onChangeRef.current(formData);
        shouldCallOnChangeRef.current = false;
      }
      return;
    }

    const controlsToProcess = [];
    const groupSwaps = [];
    dynamicFormGroups.forEach((group, groupIdx) => {
      for (const changedField of changedFields) {
        if (
          group.groupDependency === changedField &&
          changedField !== 'inputType' &&
          group.possibleControls
        ) {
          groupSwaps.push({ group, groupIdx, value: formData[changedField] });
        }
      }
      group.controls.forEach((control) => {
        for (const changedField of changedFields) {
          if (hasDependency(control, changedField)) {
            controlsToProcess.push({
              control,
              changedField,
              triggerValue: formData[changedField],
            });
            break;
          }
        }
      });
    });

    if (controlsToProcess.length === 0 && groupSwaps.length === 0) {
      if (shouldCallOnChangeRef.current && onChangeRef.current) {
        onChangeRef.current(formData);
        shouldCallOnChangeRef.current = false;
      }
      return;
    }

    const currentResolve = ++resolveCounterRef.current;

    const resolve = async () => {
      const loadingNames = {};
      controlsToProcess.forEach(({ control }) => {
        if (control.fetchOptions || control.fetchValue) {
          loadingNames[control.name] = true;
        }
      });
      if (Object.keys(loadingNames).length > 0) setLoadingControls(loadingNames);

      let updatedGroups = [...dynamicFormGroups];
      let groupsChanged = false;
      for (const { group, groupIdx, value } of groupSwaps) {
        if (!(value in group.possibleControls)) continue;
        updatedGroups[groupIdx] = { ...group, controls: cloneDeep(group.possibleControls[value]) };
        groupsChanged = true;
      }

      let optionsFetched = false;
      for (const { control, triggerValue } of controlsToProcess) {
        if (control.fetchOptions) {
          try {
            const options = await control.fetchOptions(triggerValue, {
              target: control.dependency || control.dependencies?.[0],
            });
            control.options = options;
            optionsFetched = true;
          } catch (err) {
            console.error('fetchOptions error:', err);
          }
        }
      }

      const newValues = {};
      // Side-channel for fetchValue results that carry provenance as well as a
      // value. Shape: { [bucket]: { [key]: data } }. Deliberately opaque — the
      // envelope names its own destination so this adapter needs no knowledge
      // of the domain, and modules/Common stays free of feature imports.
      const newBuckets = {};
      for (const { control, changedField, triggerValue } of controlsToProcess) {
        if (control.fetchValue) {
          try {
            let newValue;
            if (control.dependency) {
              newValue = await control.fetchValue(triggerValue, formDataRef.current);
            } else {
              newValue = await control.fetchValue(changedField, triggerValue, formDataRef.current);
            }
            if (isMetaEnvelope(newValue)) {
              newValues[control.name] = newValue.value;
              const { bucket, key, data } = newValue.meta;
              if (bucket && key) {
                newBuckets[bucket] = { ...newBuckets[bucket], [key]: data };
              }
            } else {
              newValues[control.name] = newValue;
            }
          } catch (err) {
            console.error('fetchValue error:', err);
          }
        }
      }

      if (resolveCounterRef.current !== currentResolve) return;

      if (unmountedRef.current) {
        if (shouldCallOnChangeRef.current && onChangeRef.current) {
          onChangeRef.current({
            ...formDataRef.current,
            ...newValues,
            ...mergeBuckets(formDataRef.current, newBuckets),
          });
          shouldCallOnChangeRef.current = false;
        }
        return;
      }

      setLoadingControls({});

      const mergedData = {
        ...formDataRef.current,
        ...newValues,
        // Merged into what is already there, never replacing it: two fields
        // resolving in separate passes must not erase each other's entries.
        ...mergeBuckets(formDataRef.current, newBuckets),
      };
      const newErrors = { ...errorsRef.current };
      const currentSchema = validationSchemaRef.current;
      for (const key of [...changedFields, ...Object.keys(newValues)]) {
        const fieldSchema = currentSchema.shape[key];
        if (fieldSchema) {
          try {
            fieldSchema.parse(mergedData[key]);
            delete newErrors[key];
          } catch (error) {
            if (error?.errors?.[0]?.message && touchedFieldsRef.current.has(key)) {
              newErrors[key] = error.errors[0].message;
            }
          }
        }
      }
      setErrors(newErrors);

      const actualNewValues = {};
      for (const [key, value] of Object.entries(newValues)) {
        if (formDataRef.current[key] !== value) actualNewValues[key] = value;
      }

      // Buckets must ride the STATE update, not just `mergedData`. mergedData
      // only reaches onChange in the !hasMorePending branch below; in the normal
      // cascade (a fetchValue actually changed a field) hasMorePending is true,
      // so mergedData was dropped and the next pass streamed a formData that had
      // never contained the buckets. Provenance was recorded and then silently
      // thrown away for every legacy-config form.
      const bucketUpdate = mergeBuckets(formDataRef.current, newBuckets);
      let hasMorePending = false;
      if (Object.keys(actualNewValues).length > 0 || Object.keys(bucketUpdate).length > 0) {
        setFormData((prev) => ({ ...prev, ...actualNewValues, ...mergeBuckets(prev, newBuckets) }));
        hasMorePending = true;
      }
      if (groupsChanged) {
        setDynamicFormGroups(updatedGroups);
        hasMorePending = true;
      } else if (optionsFetched) {
        setDynamicFormGroups([...updatedGroups]);
      }

      if (!hasMorePending && shouldCallOnChangeRef.current && onChangeRef.current) {
        onChangeRef.current(mergedData);
        shouldCallOnChangeRef.current = false;
      }
    };

    resolve();
  }, [formData, dynamicFormGroups]);

  // Auto-dismiss the green "Saved" feedback.
  useEffect(() => {
    if (!submitSuccess) return;
    const t = setTimeout(() => setSubmitSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [submitSuccess]);

  const validateField = (fieldName, value) => {
    const fieldSchema = validationSchema.shape[fieldName];
    if (!fieldSchema) return false;
    try {
      fieldSchema.parse(value);
      return false;
    } catch (error) {
      if (error?.errors?.[0]?.message) return error.errors[0].message;
    }
  };

  const handleChange = (fieldName, value) => {
    touchedFieldsRef.current.add(fieldName);
    shouldCallOnChangeRef.current = true;
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    const error = validateField(fieldName, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (error) next[fieldName] = error;
      else delete next[fieldName];
      return next;
    });
  };

  const handleBlur = (fieldName) => {
    touchedFieldsRef.current.add(fieldName);
    const error = validateField(fieldName, formDataRef.current[fieldName]);
    setErrors((prev) => {
      const next = { ...prev };
      if (error) next[fieldName] = error;
      else delete next[fieldName];
      return next;
    });
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (Object.keys(loadingControls).length > 0) return;
    const result = validationSchema.safeParse(formData);
    if (!result.success) {
      const newErrors = {};
      result.error.errors.forEach((err) => {
        newErrors[err.path[0]] = err.message;
      });
      setErrors(newErrors);
      return;
    }
    setErrors({});
    if (onSubmit) onSubmit(formData);
  };

  const hasLoadingControls = () => Object.keys(loadingControls).length > 0;
  const isLoading = () => submitting || loading || hasLoadingControls();
  const isSubmitBtnDisabled = () => isLoading() || Object.keys(errors).length > 0;

  return (
    <form onSubmit={handleSubmit}>
      {readOnly && (
        <div className="mb-4 p-2 flex items-center gap-2 rounded-md border bg-warningSubtle border-warningBorder text-warningEmphasis text-xs">
          <FontAwesomeIcon icon={faIcons.faEye} />
          {t('common:form.readOnlyPreview', "Read-only preview — changes won't be saved.")}
        </div>
      )}
      {dynamicFormGroups.map((group, groupIndex) => {
        if (!group.controls?.length) return null;
        return (
          <div
            key={groupIndex}
            className={`mb-5 ${group.className || ''}`}
          >
            {/* Rendered as a <p>, not <h3>, to avoid inheriting global
                heading styles (Bootstrap + app-level h3 = brand color +
                larger font). See FormSection.jsx for the full rationale. */}
            {group.header ? <p className={sectionTitleClass}>{group.header}</p> : null}
            <div className="grid grid-cols-12 gap-x-6">
              {group.controls.map((control) => {
                const disabled =
                  readOnly ||
                  control.disabled ||
                  !!loadingControls[control.name] ||
                  (control.shouldBeDisabled ? control.shouldBeDisabled(formData) : false);
                const fieldLoading = !!loadingControls[control.name];
                if (control.shouldBeHidden && control.shouldBeHidden(formData)) return null;

                return (
                  <FieldWrapper
                    key={`${groupIndex}-${control.name}`}
                    control={control}
                    value={formData[control.name] ?? ''}
                    error={extraErrors?.[control.name] ?? errors[control.name]}
                    fieldLoading={fieldLoading}
                    disabled={disabled || loading}
                    onChange={(v) => handleChange(control.name, v)}
                    onBlur={() => handleBlur(control.name)}
                    anchorKind={anchorKind}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      {children}
      {!hideButton && !readOnly ? (
        <div className="flex items-center gap-3 pt-2">
          {actions ? actions.map((a, i) => <React.Fragment key={i}>{a}</React.Fragment>) : null}
          <button
            id="lastButton"
            type="submit"
            disabled={isSubmitBtnDisabled()}
            className="px-4 py-2 text-sm font-bold text-white bg-primaryColor rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2"
          >
            <FontAwesomeIcon
              icon={
                submitting || hasLoadingControls() ? faIcons.faSpinner : btnIcon || faIcons.faSave
              }
              className={`w-3.5 h-3.5 ${submitting || hasLoadingControls() ? 'animate-spin' : ''}`}
            />
            {submitting
              ? t('common:actions.saving', 'Saving…')
              : hasLoadingControls()
                ? t('common:loading.default', 'Loading...')
                : btnName}
          </button>
          {submitSuccess && !isLoading() ? (
            <span className="inline-flex items-center gap-1 text-sm text-green-600">
              <FontAwesomeIcon icon={faIcons.faCheckCircle} />
              {t('common:form.savedSuccessfully', 'Saved Successfully')}
            </span>
          ) : null}
        </div>
      ) : null}
      {/* `formShown` is part of the v1 prop surface; touch it so eslint
          doesn't flag the unused destructure. */}
      {formShown != null ? null : null}
    </form>
  );
});

export default LegacyFormGroups;
